import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
    Activity,
    AlertCircle,
    ArrowDownRight,
    ArrowUpDown,
    ArrowUpRight,
    Bot,
    Braces,
    CalendarDays,
    Clock3,
    FileWarning,
    ListFilter,
    RefreshCw,
    Search,
    Settings2,
    ShieldAlert,
    Sparkles,
    Wrench,
} from "lucide-react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
    createParameterFailureAggregation,
    mergeParameterFailureBatch,
    toParameterFailureInsights,
    type ParameterFailureAggregation,
} from "./ParameterFailureAggregation";
import { TOOL_CLASSIFICATION_SOURCE_VERSION } from "./ToolClassification";
import {
    LANGFUSE_ENVIRONMENTS,
    type LangfuseSettings,
    type ParameterFailureBatch,
    type ParameterFailureInsights,
    type ParameterFailureMetric,
    type ToolCategory,
    type ToolInsights,
    type ToolMetric,
} from "./Types";

const STORAGE_KEY = "jander-tool-insights.settings.v1";
const PARAMETER_FAILURE_SCAN_VERSION = "v2-page-10";
const DEFAULT_SETTINGS: LangfuseSettings = {
    baseUrl: LANGFUSE_ENVIRONMENTS.test.baseUrl,
    publicKey: "",
    secretKey: "",
};

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const PARAMETER_FAILURE_CONFIRMATION_THRESHOLD_MS = 3 * HOUR_MS;
const TIME_RANGE_OPTIONS = [
    { value: "5m", badge: "5m", label: "过去 5 分钟", shortLabel: "5 分钟", durationMs: 5 * MINUTE_MS },
    { value: "30m", badge: "30m", label: "过去 30 分钟", shortLabel: "30 分钟", durationMs: 30 * MINUTE_MS },
    { value: "1h", badge: "1h", label: "过去 1 小时", shortLabel: "1 小时", durationMs: HOUR_MS },
    { value: "3h", badge: "3h", label: "过去 3 小时", shortLabel: "3 小时", durationMs: 3 * HOUR_MS },
    { value: "1d", badge: "1d", label: "过去 24 小时", shortLabel: "24 小时", durationMs: DAY_MS },
    { value: "7d", badge: "7d", label: "过去 7 天", shortLabel: "7 天", durationMs: 7 * DAY_MS },
    { value: "30d", badge: "30d", label: "过去 30 天", shortLabel: "30 天", durationMs: 30 * DAY_MS },
    { value: "90d", badge: "90d", label: "过去 90 天", shortLabel: "90 天", durationMs: 90 * DAY_MS },
    { value: "1y", badge: "1y", label: "过去 1 年", shortLabel: "1 年", durationMs: 365 * DAY_MS },
] as const;

type TimeRangePreset = (typeof TIME_RANGE_OPTIONS)[number]["value"];
type TimeRangeSelection = TimeRangePreset | "custom";

interface CustomTimeRange {
    fromTimestamp: string;
    toTimestamp: string;
}

const trendConfig = {
    calls: { label: "工具调用", color: "var(--insight-primary)" },
} satisfies ChartConfig;

const rankingConfig = {
    calls: { label: "调用次数", color: "var(--insight-primary)" },
} satisfies ChartConfig;

const TOOL_CATEGORY_LABELS: Record<ToolCategory, string> = {
    frontend: "前端工具",
    backend: "后端工具",
    other: "其他",
};

const TOOL_CATEGORY_STYLES: Record<ToolCategory, string> = {
    frontend: "border-blue-200 bg-blue-50 text-blue-700",
    backend: "border-emerald-200 bg-emerald-50 text-emerald-700",
    other: "border-slate-200 bg-slate-50 text-slate-700",
};

type ToolCategoryFilter = "all" | ToolCategory;
type FailureSort = "failureCount" | "failureRate";
type ToolTableTab = "ranking" | "executionFailures" | "parameterFailures";

interface LoadedInsightsRange {
    selectionKey: string;
    durationMs: number;
    fromTimestamp: string;
    toTimestamp: string;
}

interface ActiveTimeRange {
    selectionKey: string;
    durationMs: number;
    shortLabel: string;
    fromTimestamp?: string;
    toTimestamp?: string;
}

interface ParameterFailureRow extends ParameterFailureMetric {
    executedCalls: number;
    failureRate: number;
}

interface ParameterFailureScanCheckpoint {
    requestKey: string;
    nextPage: number;
    aggregation: ParameterFailureAggregation;
}

function readSettings(): LangfuseSettings {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        const parsed = saved ? (JSON.parse(saved) as Partial<LangfuseSettings>) : {};
        const normalizedBaseUrl = parsed.baseUrl?.replace(/\/$/, "");
        const selectedEnvironment = Object.values(LANGFUSE_ENVIRONMENTS).find(
            ({ baseUrl }) => baseUrl === normalizedBaseUrl,
        );
        return {
            ...DEFAULT_SETTINGS,
            ...parsed,
            baseUrl: selectedEnvironment?.baseUrl ?? DEFAULT_SETTINGS.baseUrl,
        };
    } catch {
        return DEFAULT_SETTINGS;
    }
}

function formatNumber(value: number): string {
    return new Intl.NumberFormat("zh-CN").format(value);
}

function formatPercent(value: number): string {
    return `${(value * 100).toFixed(value > 0.01 ? 1 : 2)}%`;
}

function formatLatency(value: number | null): string {
    if (value === null) return "—";
    return value >= 1000 ? `${(value / 1000).toFixed(2)} s` : `${Math.round(value)} ms`;
}

function dateLabel(value: string, durationMs: number): string {
    const normalized = value.includes("T") ? value : value.replace(" ", "T");
    const date = new Date(normalized);
    if (!Number.isFinite(date.getTime())) return value;
    if (durationMs <= 3 * DAY_MS) {
        return new Intl.DateTimeFormat("zh-CN", { hour: "2-digit", minute: "2-digit", hour12: false }).format(date);
    }
    if (durationMs <= 90 * DAY_MS) {
        return new Intl.DateTimeFormat("zh-CN", { month: "numeric", day: "numeric" }).format(date);
    }
    return new Intl.DateTimeFormat("zh-CN", { year: "numeric", month: "numeric" }).format(date);
}

function toDateTimeLocalValue(date: Date): string {
    const offsetMs = date.getTimezoneOffset() * MINUTE_MS;
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function formatCustomRangeLabel(range: CustomTimeRange): string {
    const formatter = new Intl.DateTimeFormat("zh-CN", {
        month: "numeric",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
    return `${formatter.format(new Date(range.fromTimestamp))} – ${formatter.format(new Date(range.toTimestamp))}`;
}

function StatCard({
    label,
    value,
    detail,
    icon,
    tone = "default",
}: {
    label: string;
    value: string;
    detail: string;
    icon: ReactNode;
    tone?: "default" | "danger";
}) {
    return (
        <Card className="stat-card gap-4 py-5">
            <CardHeader className="grid-cols-[1fr_auto] gap-3 px-5">
                <div className="space-y-2">
                    <CardDescription>{label}</CardDescription>
                    <CardTitle className={tone === "danger" ? "text-3xl text-rose-600" : "text-3xl"}>{value}</CardTitle>
                </div>
                <div className={tone === "danger" ? "stat-icon stat-icon-danger" : "stat-icon"}>{icon}</div>
            </CardHeader>
            <CardContent className="px-5 text-xs text-muted-foreground">{detail}</CardContent>
        </Card>
    );
}

function SettingsDialog({
    open,
    settings,
    onOpenChange,
    onSave,
}: {
    open: boolean;
    settings: LangfuseSettings;
    onOpenChange: (open: boolean) => void;
    onSave: (settings: LangfuseSettings) => void;
}) {
    const [draft, setDraft] = useState(settings);

    useEffect(() => {
        if (open) setDraft(settings);
    }, [open, settings]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-xl">
                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        onSave({
                            baseUrl: draft.baseUrl.trim(),
                            publicKey: draft.publicKey.trim(),
                            secretKey: draft.secretKey.trim(),
                        });
                    }}
                >
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Settings2 className="size-5" /> Langfuse 连接设置
                        </DialogTitle>
                        <DialogDescription>用于读取 Jander Trace 下的 TOOL 和 Agent observations。</DialogDescription>
                    </DialogHeader>

                    <div className="my-6 grid gap-5">
                        <div className="grid gap-2">
                            <Label htmlFor="langfuse-environment">Langfuse 环境</Label>
                            <Select value={draft.baseUrl} onValueChange={(baseUrl) => setDraft({ ...draft, baseUrl })}>
                                <SelectTrigger id="langfuse-environment" className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.values(LANGFUSE_ENVIRONMENTS).map((environment) => (
                                        <SelectItem key={environment.baseUrl} value={environment.baseUrl}>
                                            {environment.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="break-all text-xs text-muted-foreground">{draft.baseUrl}</p>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="grid gap-2">
                                <Label htmlFor="public-key">Public Key</Label>
                                <Input
                                    id="public-key"
                                    type="password"
                                    required
                                    autoComplete="off"
                                    value={draft.publicKey}
                                    onChange={(event) => setDraft({ ...draft, publicKey: event.target.value })}
                                    placeholder="pk-lf-..."
                                />
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="secret-key">Secret Key</Label>
                                <Input
                                    id="secret-key"
                                    type="password"
                                    required
                                    autoComplete="off"
                                    value={draft.secretKey}
                                    onChange={(event) => setDraft({ ...draft, secretKey: event.target.value })}
                                    placeholder="sk-lf-..."
                                />
                            </div>
                        </div>
                        <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900">
                            <ShieldAlert className="mt-0.5 size-4 shrink-0" />
                            <span>
                                Key 仅保存在当前浏览器 localStorage，并只会发往所选 Langfuse
                                环境；不要在共用设备上使用。
                            </span>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            取消
                        </Button>
                        <Button type="submit">保存并连接</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function CustomTimeRangeDialog({
    open,
    range,
    onOpenChange,
    onApply,
}: {
    open: boolean;
    range: CustomTimeRange | null;
    onOpenChange: (open: boolean) => void;
    onApply: (range: CustomTimeRange) => void;
}) {
    const [fromValue, setFromValue] = useState("");
    const [toValue, setToValue] = useState("");
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        const to = range ? new Date(range.toTimestamp) : new Date();
        const from = range ? new Date(range.fromTimestamp) : new Date(to.getTime() - DAY_MS);
        setFromValue(toDateTimeLocalValue(from));
        setToValue(toDateTimeLocalValue(to));
        setValidationError(null);
    }, [open, range]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        const from = new Date(fromValue);
                        const to = new Date(toValue);
                        const durationMs = to.getTime() - from.getTime();
                        if (!Number.isFinite(durationMs) || durationMs <= 0) {
                            setValidationError("结束时间必须晚于开始时间");
                            return;
                        }
                        if (durationMs > 366 * DAY_MS) {
                            setValidationError("自定义时间范围不能超过 366 天");
                            return;
                        }
                        onApply({ fromTimestamp: from.toISOString(), toTimestamp: to.toISOString() });
                    }}
                >
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Clock3 className="size-5" /> 自定义时间范围
                        </DialogTitle>
                        <DialogDescription>
                            参数失败分析仅支持不超过 24 小时的范围；其他统计最多支持 366 天。
                        </DialogDescription>
                    </DialogHeader>
                    <div className="my-6 grid gap-5 sm:grid-cols-2">
                        <div className="grid gap-2">
                            <Label htmlFor="custom-range-from">开始时间</Label>
                            <Input
                                id="custom-range-from"
                                type="datetime-local"
                                step="60"
                                required
                                value={fromValue}
                                onChange={(event) => setFromValue(event.target.value)}
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="custom-range-to">结束时间</Label>
                            <Input
                                id="custom-range-to"
                                type="datetime-local"
                                step="60"
                                required
                                value={toValue}
                                onChange={(event) => setToValue(event.target.value)}
                            />
                        </div>
                        {validationError && (
                            <div className="sm:col-span-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
                                {validationError}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            取消
                        </Button>
                        <Button type="submit">应用时间范围</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ParameterFailureConfirmationDialog({
    open,
    rangeLabel,
    onCancel,
    onConfirm,
}: {
    open: boolean;
    rangeLabel: string;
    onCancel: () => void;
    onConfirm: () => void;
}) {
    return (
        <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onCancel()}>
            <DialogContent showCloseButton={false} className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileWarning className="size-5 text-amber-600" /> 确认扫描较长时间范围？
                    </DialogTitle>
                    <DialogDescription>
                        当前选择“{rangeLabel}”。参数失败分析需要逐页读取 Agent Output，可能持续数分钟并产生较多
                        Langfuse 和 Cloudflare 请求。
                    </DialogDescription>
                </DialogHeader>
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
                    请等待本次扫描完成，不要频繁刷新、切换后立即重试或重复发起扫描。日常排查建议优先使用过去 3
                    小时。
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onCancel}>
                        取消
                    </Button>
                    <Button type="button" onClick={onConfirm}>
                        确认并开始扫描
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ToolsTable({
    tools,
    totalCalls,
    failuresOnly,
}: {
    tools: ToolMetric[];
    totalCalls: number;
    failuresOnly: boolean;
}) {
    if (!tools.length) {
        return <div className="grid min-h-48 place-items-center text-sm text-muted-foreground">当前范围内没有数据</div>;
    }

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>工具名</TableHead>
                    <TableHead className="hidden md:table-cell">类别</TableHead>
                    <TableHead className="text-right">{failuresOnly ? "失败次数" : "调用次数"}</TableHead>
                    <TableHead className="hidden text-right sm:table-cell">
                        {failuresOnly ? "失败率" : "占比"}
                    </TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {tools.slice(0, 100).map((tool, index) => {
                    const value = failuresOnly ? tool.failures : tool.calls;
                    const ratio = failuresOnly
                        ? tool.calls
                            ? tool.failures / tool.calls
                            : 0
                        : totalCalls
                          ? tool.calls / totalCalls
                          : 0;
                    return (
                        <TableRow key={tool.name}>
                            <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    <Braces className="size-4 text-muted-foreground" />
                                    <code className="text-[13px] font-medium">{tool.name}</code>
                                </div>
                            </TableCell>
                            <TableCell className="hidden md:table-cell">
                                <Badge variant="outline" className={TOOL_CATEGORY_STYLES[tool.category]}>
                                    {TOOL_CATEGORY_LABELS[tool.category]}
                                </Badge>
                            </TableCell>
                            <TableCell
                                className={
                                    failuresOnly ? "text-right font-semibold text-rose-600" : "text-right font-semibold"
                                }
                            >
                                {formatNumber(value)}
                            </TableCell>
                            <TableCell className="hidden text-right text-muted-foreground sm:table-cell">
                                {formatPercent(ratio)}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}

function ParameterFailuresTable({ rows }: { rows: ParameterFailureRow[] }) {
    if (!rows.length) {
        return (
            <div className="grid min-h-48 place-items-center px-6 text-center text-sm text-muted-foreground">
                当前扫描范围内没有识别到结构明确的参数 Schema 失败
            </div>
        );
    }

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>工具名</TableHead>
                    <TableHead className="hidden md:table-cell">类别</TableHead>
                    <TableHead className="text-right">参数失败次数</TableHead>
                    <TableHead className="hidden text-right sm:table-cell">进入执行次数</TableHead>
                    <TableHead className="text-right">参数失败率</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.slice(0, 100).map((row, index) => (
                    <TableRow key={row.name}>
                        <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                        <TableCell>
                            <div className="flex items-center gap-2">
                                <Braces className="size-4 text-muted-foreground" />
                                <code className="text-[13px] font-medium">{row.name}</code>
                            </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                            <Badge variant="outline" className={TOOL_CATEGORY_STYLES[row.category]}>
                                {TOOL_CATEGORY_LABELS[row.category]}
                            </Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold text-amber-700">
                            {formatNumber(row.failures)}
                        </TableCell>
                        <TableCell className="hidden text-right text-muted-foreground sm:table-cell">
                            {formatNumber(row.executedCalls)}
                        </TableCell>
                        <TableCell className="text-right font-semibold">{formatPercent(row.failureRate)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

export function Dashboard() {
    const [settings, setSettings] = useState<LangfuseSettings>(readSettings);
    const [settingsOpen, setSettingsOpen] = useState(() => !readSettings().secretKey);
    const [timeRangeSelection, setTimeRangeSelection] = useState<TimeRangeSelection>("30d");
    const [customTimeRange, setCustomTimeRange] = useState<CustomTimeRange | null>(null);
    const [customTimeRangeOpen, setCustomTimeRangeOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState<ToolCategoryFilter>("all");
    const [tableTab, setTableTab] = useState<ToolTableTab>("ranking");
    const [failureSort, setFailureSort] = useState<FailureSort>("failureCount");
    const [parameterFailureSort, setParameterFailureSort] = useState<FailureSort>("failureCount");
    const [data, setData] = useState<ToolInsights | null>(null);
    const [loadedInsightsRange, setLoadedInsightsRange] = useState<LoadedInsightsRange | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [parameterFailureData, setParameterFailureData] = useState<ParameterFailureInsights | null>(null);
    const [parameterFailureLoadedKey, setParameterFailureLoadedKey] = useState<string | null>(null);
    const [parameterFailureLoading, setParameterFailureLoading] = useState(false);
    const [parameterFailureError, setParameterFailureError] = useState<string | null>(null);
    const [parameterFailureReload, setParameterFailureReload] = useState(0);
    const [parameterFailureConfirmationOpen, setParameterFailureConfirmationOpen] = useState(false);
    const [parameterFailureConfirmedSelectionKey, setParameterFailureConfirmedSelectionKey] = useState<string | null>(
        null,
    );
    const parameterFailureCheckpointRef = useRef<ParameterFailureScanCheckpoint | null>(null);

    const configured = Boolean(settings.baseUrl && settings.publicKey && settings.secretKey);
    const environmentLabel = Object.values(LANGFUSE_ENVIRONMENTS).find(
        ({ baseUrl }) => baseUrl === settings.baseUrl,
    )?.label;
    const selectedTimeRange = TIME_RANGE_OPTIONS.find((option) => option.value === timeRangeSelection);
    const activeTimeRange = useMemo<ActiveTimeRange>(() => {
        if (timeRangeSelection === "custom" && customTimeRange) {
            const durationMs =
                new Date(customTimeRange.toTimestamp).getTime() - new Date(customTimeRange.fromTimestamp).getTime();
            return {
                selectionKey: `custom:${customTimeRange.fromTimestamp}:${customTimeRange.toTimestamp}`,
                durationMs,
                fromTimestamp: customTimeRange.fromTimestamp,
                toTimestamp: customTimeRange.toTimestamp,
                shortLabel: formatCustomRangeLabel(customTimeRange),
            };
        }
        const preset = selectedTimeRange ?? TIME_RANGE_OPTIONS[6];
        return {
            selectionKey: preset.value,
            durationMs: preset.durationMs,
            shortLabel: preset.shortLabel,
        };
    }, [customTimeRange, selectedTimeRange, timeRangeSelection]);
    const parameterFailureRangeSupported = activeTimeRange.durationMs <= DAY_MS;
    const parameterFailureRequiresConfirmation =
        activeTimeRange.durationMs > PARAMETER_FAILURE_CONFIRMATION_THRESHOLD_MS && parameterFailureRangeSupported;
    const parameterFailureLongRangeConfirmed =
        !parameterFailureRequiresConfirmation || parameterFailureConfirmedSelectionKey === activeTimeRange.selectionKey;

    const loadInsights = useCallback(
        async (signal?: AbortSignal) => {
            if (!configured) return;
            setLoading(true);
            setError(null);
            try {
                const to = activeTimeRange.toTimestamp ? new Date(activeTimeRange.toTimestamp) : new Date();
                const from = activeTimeRange.fromTimestamp
                    ? new Date(activeTimeRange.fromTimestamp)
                    : new Date(to.getTime() - activeTimeRange.durationMs);
                const response = await fetch("/api/tool-insights", {
                    method: "POST",
                    headers: { "content-type": "application/json" },
                    body: JSON.stringify({
                        ...settings,
                        fromTimestamp: from.toISOString(),
                        toTimestamp: to.toISOString(),
                    }),
                    signal,
                });
                const result = (await response.json()) as ToolInsights & { error?: string };
                if (!response.ok) throw new Error(result.error ?? "获取数据失败");
                setData(result);
                setLoadedInsightsRange({
                    selectionKey: activeTimeRange.selectionKey,
                    durationMs: to.getTime() - from.getTime(),
                    fromTimestamp: from.toISOString(),
                    toTimestamp: to.toISOString(),
                });
            } catch (cause) {
                if (cause instanceof DOMException && cause.name === "AbortError") return;
                setError(cause instanceof Error ? cause.message : "获取数据失败");
            } finally {
                setLoading(false);
            }
        },
        [activeTimeRange, configured, settings],
    );

    useEffect(() => {
        const controller = new AbortController();
        void loadInsights(controller.signal);
        return () => controller.abort();
    }, [loadInsights]);

    useEffect(() => {
        if (
            tableTab === "parameterFailures" &&
            parameterFailureRequiresConfirmation &&
            !parameterFailureLongRangeConfirmed
        ) {
            setParameterFailureConfirmationOpen(true);
        }
    }, [parameterFailureLongRangeConfirmed, parameterFailureRequiresConfirmation, tableTab]);

    useEffect(() => {
        if (
            tableTab !== "parameterFailures" ||
            !parameterFailureRangeSupported ||
            !parameterFailureLongRangeConfirmed ||
            !configured ||
            loadedInsightsRange?.selectionKey !== activeTimeRange.selectionKey ||
            loadedInsightsRange.durationMs > DAY_MS
        ) {
            return;
        }

        const requestKey = [
            PARAMETER_FAILURE_SCAN_VERSION,
            settings.baseUrl,
            settings.publicKey,
            settings.secretKey,
            loadedInsightsRange.fromTimestamp,
            loadedInsightsRange.toTimestamp,
        ].join(":");
        if (parameterFailureLoadedKey === requestKey) return;

        const controller = new AbortController();
        const existingCheckpoint = parameterFailureCheckpointRef.current;
        const checkpoint: ParameterFailureScanCheckpoint =
            existingCheckpoint?.requestKey === requestKey
                ? existingCheckpoint
                : {
                      requestKey,
                      nextPage: 1,
                      aggregation: createParameterFailureAggregation(),
                  };
        parameterFailureCheckpointRef.current = checkpoint;
        setParameterFailureLoading(true);
        setParameterFailureError(null);
        if (!checkpoint.aggregation.limits) setParameterFailureData(null);

        void (async () => {
            try {
                while (!controller.signal.aborted) {
                    const requestedPage = checkpoint.nextPage;
                    const response = await fetch("/api/parameter-failures", {
                        method: "POST",
                        headers: { "content-type": "application/json" },
                        body: JSON.stringify({
                            ...settings,
                            fromTimestamp: loadedInsightsRange.fromTimestamp,
                            toTimestamp: loadedInsightsRange.toTimestamp,
                            startPage: requestedPage,
                        }),
                        signal: controller.signal,
                    });
                    const batch = (await response.json()) as ParameterFailureBatch & { error?: string };
                    if (!response.ok) throw new Error(batch.error ?? "获取参数失败数据失败");

                    mergeParameterFailureBatch(checkpoint.aggregation, batch);
                    const truncationReason =
                        batch.stopReason === "observation_limit"
                            ? "observation_limit"
                            : batch.stopReason === "response_size_limit"
                              ? "response_size_limit"
                              : null;
                    setParameterFailureData(
                        toParameterFailureInsights(checkpoint.aggregation, batch.complete, truncationReason),
                    );

                    if (batch.nextPage === null) {
                        parameterFailureCheckpointRef.current = null;
                        setParameterFailureLoadedKey(requestKey);
                        setParameterFailureLoading(false);
                        return;
                    }
                    if (batch.nextPage <= requestedPage) {
                        throw new Error("参数失败扫描游标没有向前推进");
                    }
                    checkpoint.nextPage = batch.nextPage;
                }
            } catch (cause: unknown) {
                if (cause instanceof DOMException && cause.name === "AbortError") return;
                setParameterFailureError(cause instanceof Error ? cause.message : "获取参数失败数据失败");
                setParameterFailureLoading(false);
            }
        })();

        return () => controller.abort();
    }, [
        configured,
        activeTimeRange.selectionKey,
        loadedInsightsRange,
        parameterFailureLoadedKey,
        parameterFailureReload,
        parameterFailureLongRangeConfirmed,
        parameterFailureRangeSupported,
        settings,
        tableTab,
    ]);

    const categoryCounts = useMemo(() => {
        const counts: Record<ToolCategory, number> = { frontend: 0, backend: 0, other: 0 };
        for (const tool of data?.tools ?? []) counts[tool.category] += 1;
        return counts;
    }, [data]);
    const filteredTools = useMemo(() => {
        const keyword = search.trim().toLowerCase();
        return (
            data?.tools.filter(
                (tool) =>
                    (category === "all" || tool.category === category) &&
                    (!keyword || tool.name.toLowerCase().includes(keyword)),
            ) ?? []
        );
    }, [category, data, search]);
    const failedTools = useMemo(() => {
        return filteredTools.filter((tool) => tool.failures > 0).sort((left, right) => {
            const leftRate = left.calls ? left.failures / left.calls : 0;
            const rightRate = right.calls ? right.failures / right.calls : 0;
            if (failureSort === "failureRate") {
                return rightRate - leftRate || right.failures - left.failures || left.name.localeCompare(right.name);
            }
            return right.failures - left.failures || rightRate - leftRate || left.name.localeCompare(right.name);
        });
    }, [failureSort, filteredTools]);
    const parameterFailureRows = useMemo(() => {
        const keyword = search.trim().toLowerCase();
        const executedCallsByName = new Map((data?.tools ?? []).map((tool) => [tool.name, tool.calls]));
        return (parameterFailureData?.failures ?? [])
            .filter(
                (item) =>
                    (category === "all" || item.category === category) &&
                    (!keyword || item.name.toLowerCase().includes(keyword)),
            )
            .map((item) => {
                const executedCalls = executedCallsByName.get(item.name) ?? 0;
                const attempts = executedCalls + item.failures;
                return {
                    ...item,
                    executedCalls,
                    failureRate: attempts ? item.failures / attempts : 0,
                };
            })
            .sort((left, right) => {
                if (parameterFailureSort === "failureRate") {
                    return (
                        right.failureRate - left.failureRate ||
                        right.failures - left.failures ||
                        left.name.localeCompare(right.name)
                    );
                }
                return (
                    right.failures - left.failures ||
                    right.failureRate - left.failureRate ||
                    left.name.localeCompare(right.name)
                );
            });
    }, [category, data, parameterFailureData, parameterFailureSort, search]);
    const parameterCategoryCounts = useMemo(() => {
        const counts: Record<ToolCategory, number> = { frontend: 0, backend: 0, other: 0 };
        for (const tool of parameterFailureData?.failures ?? []) counts[tool.category] += 1;
        return counts;
    }, [parameterFailureData]);
    const visibleCategoryCounts = tableTab === "parameterFailures" ? parameterCategoryCounts : categoryCounts;
    const visibleAllCategoryCount = tableTab === "parameterFailures"
        ? parameterFailureData?.failures.length ?? 0
        : data?.activeTools ?? 0;
    const topTools = data?.tools.slice(0, 8) ?? [];
    const errorRate = data?.totalCalls ? data.failedCalls / data.totalCalls : 0;

    return (
        <TooltipProvider>
            <div className="min-h-screen bg-[var(--insight-canvas)] text-[var(--insight-ink)]">
                <header className="border-b border-black/6 bg-white/90 backdrop-blur">
                    <div className="mx-auto flex max-w-[1480px] items-center justify-between px-5 py-4 lg:px-8">
                        <div className="flex items-center gap-3">
                            <div className="brand-mark">
                                <Bot className="size-5" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold tracking-tight">Jander</span>
                                    <span className="text-muted-foreground">/</span>
                                    <span className="text-sm text-muted-foreground">Tool Insights</span>
                                </div>
                                <p className="mt-0.5 text-xs text-muted-foreground">从工具使用中找到 Skill 改进线索</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Badge variant="outline" className={configured && !error ? "status-ok" : "status-idle"}>
                                <span className="size-1.5 rounded-full bg-current" />
                                {configured && !error ? `${environmentLabel} · 已配置` : "未连接"}
                            </Badge>
                            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
                                <Settings2 />
                                <span className="hidden sm:inline">设置</span>
                            </Button>
                        </div>
                    </div>
                </header>

                <main className="mx-auto max-w-[1480px] px-5 py-8 lg:px-8 lg:py-10">
                    <section className="mb-8 flex flex-col justify-between gap-5 md:flex-row md:items-end">
                        <div>
                            <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
                                <Sparkles className="size-3.5" /> Observability snapshot
                            </div>
                            <h1 className="text-3xl font-semibold tracking-[-0.035em] sm:text-4xl">工具调用洞察</h1>
                            <p className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground">
                                统计 <code className="filter-code">AI 应用搭建</code> Trace 中的独立 TOOL observation，
                                工具名来自 observation.name。
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Select
                                value={timeRangeSelection === "custom" ? "custom-active" : timeRangeSelection}
                                onValueChange={(value) => {
                                    if (value === "custom") {
                                        setCustomTimeRangeOpen(true);
                                        return;
                                    }
                                    setParameterFailureConfirmedSelectionKey(null);
                                    setTimeRangeSelection(value as TimeRangePreset);
                                }}
                            >
                                <SelectTrigger className="min-w-44 bg-white">
                                    <Clock3 className="size-4" />
                                    <SelectValue>{activeTimeRange.shortLabel}</SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                    {timeRangeSelection === "custom" && (
                                        <SelectItem value="custom-active" disabled className="hidden">
                                            {activeTimeRange.shortLabel}
                                        </SelectItem>
                                    )}
                                    {TIME_RANGE_OPTIONS.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            <span className="flex items-center gap-3">
                                                <span className="min-w-10 rounded-md bg-slate-100 px-2 py-1 text-center text-xs font-medium text-slate-600">
                                                    {option.badge}
                                                </span>
                                                <span>{option.label}</span>
                                            </span>
                                        </SelectItem>
                                    ))}
                                    <SelectItem value="custom">
                                        <span className="flex items-center gap-3">
                                            <span className="grid min-w-10 place-items-center rounded-md bg-slate-100 px-2 py-1 text-slate-600">
                                                <CalendarDays className="size-4" />
                                            </span>
                                            <span>自定义时间</span>
                                        </span>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                            {timeRangeSelection === "custom" && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    className="bg-white"
                                    aria-label="编辑自定义时间范围"
                                    title="编辑自定义时间范围"
                                    onClick={() => setCustomTimeRangeOpen(true)}
                                >
                                    <CalendarDays />
                                </Button>
                            )}
                            <Button
                                className="refresh-button"
                                onClick={() => void loadInsights()}
                                disabled={!configured || loading}
                            >
                                <RefreshCw className={loading ? "animate-spin" : ""} />
                                刷新数据
                            </Button>
                        </div>
                    </section>

                    {!configured && (
                        <Card className="mb-6 border-dashed border-emerald-300 bg-emerald-50/70">
                            <CardContent className="flex flex-col items-start justify-between gap-4 py-1 sm:flex-row sm:items-center">
                                <div className="flex gap-3">
                                    <Settings2 className="mt-0.5 size-5 text-emerald-700" />
                                    <div>
                                        <p className="font-medium">先连接 Langfuse</p>
                                        <p className="mt-1 text-sm text-muted-foreground">
                                            默认选中测试环境，补充 Key 即可加载真实数据。
                                        </p>
                                    </div>
                                </div>
                                <Button onClick={() => setSettingsOpen(true)}>打开设置</Button>
                            </CardContent>
                        </Card>
                    )}

                    {error && (
                        <div className="mb-6 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
                            <AlertCircle className="mt-0.5 size-4 shrink-0" />
                            <div className="flex-1">
                                <strong>连接失败：</strong>
                                {error}
                            </div>
                            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
                                检查设置
                            </Button>
                        </div>
                    )}

                    <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                        {loading && !data ? (
                            Array.from({ length: 4 }).map((_, index) => (
                                <Card key={index} className="gap-4 py-5">
                                    <CardContent className="space-y-4 px-5">
                                        <Skeleton className="h-4 w-24" />
                                        <Skeleton className="h-9 w-32" />
                                        <Skeleton className="h-3 w-40" />
                                    </CardContent>
                                </Card>
                            ))
                        ) : (
                            <>
                                <StatCard
                                    label="工具调用总量"
                                    value={formatNumber(data?.totalCalls ?? 0)}
                                    detail={`Trace: ${data?.traceName ?? "AI 应用搭建"}`}
                                    icon={<Activity />}
                                />
                                <StatCard
                                    label="活跃工具"
                                    value={formatNumber(data?.activeTools ?? 0)}
                                    detail="当前时间范围内有调用的工具"
                                    icon={<Wrench />}
                                />
                                <StatCard
                                    label="执行失败"
                                    value={formatNumber(data?.failedCalls ?? 0)}
                                    detail={`执行失败率 ${formatPercent(errorRate)}，不含参数 Schema 失败`}
                                    icon={<AlertCircle />}
                                    tone="danger"
                                />
                                <StatCard
                                    label="平均执行耗时"
                                    value={formatLatency(data?.averageLatencyMs ?? null)}
                                    detail="TOOL observation 的 start 至 end"
                                    icon={<Clock3 />}
                                />
                            </>
                        )}
                    </section>

                    <section className="mt-5 grid gap-5 xl:grid-cols-[minmax(0,1.65fr)_minmax(360px,1fr)]">
                        <Card className="chart-card">
                            <CardHeader>
                                <CardTitle>调用趋势</CardTitle>
                                <CardDescription>按时间范围自动选择粒度，用于识别需求波动和上线影响。</CardDescription>
                                <CardAction>
                                    <Badge variant="secondary">{activeTimeRange.shortLabel}</Badge>
                                </CardAction>
                            </CardHeader>
                            <CardContent>
                                <ChartContainer config={trendConfig} className="h-[310px] w-full aspect-auto">
                                    <AreaChart data={data?.trend ?? []} margin={{ left: 4, right: 12, top: 12 }}>
                                        <defs>
                                            <linearGradient id="calls-fill" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="8%" stopColor="var(--color-calls)" stopOpacity={0.28} />
                                                <stop offset="95%" stopColor="var(--color-calls)" stopOpacity={0.02} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid vertical={false} strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="date"
                                            tickFormatter={(value) =>
                                                dateLabel(
                                                    String(value),
                                                    loadedInsightsRange?.durationMs ?? activeTimeRange.durationMs,
                                                )
                                            }
                                            tickLine={false}
                                            axisLine={false}
                                            minTickGap={28}
                                        />
                                        <YAxis tickLine={false} axisLine={false} width={45} />
                                        <ChartTooltip content={<ChartTooltipContent indicator="line" />} />
                                        <Area
                                            dataKey="calls"
                                            type="monotone"
                                            fill="url(#calls-fill)"
                                            stroke="var(--color-calls)"
                                            strokeWidth={2.5}
                                        />
                                    </AreaChart>
                                </ChartContainer>
                            </CardContent>
                        </Card>

                        <Card className="chart-card">
                            <CardHeader>
                                <CardTitle>Top 工具</CardTitle>
                                <CardDescription>最常被 Agent 调用的工具。</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <ChartContainer config={rankingConfig} className="h-[310px] w-full aspect-auto">
                                    <BarChart
                                        data={[...topTools].reverse()}
                                        layout="vertical"
                                        margin={{ left: 0, right: 12 }}
                                    >
                                        <CartesianGrid horizontal={false} strokeDasharray="3 3" />
                                        <XAxis type="number" hide />
                                        <YAxis
                                            dataKey="name"
                                            type="category"
                                            tickLine={false}
                                            axisLine={false}
                                            width={142}
                                            tick={{ fontSize: 11 }}
                                        />
                                        <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                                        <Bar
                                            dataKey="calls"
                                            fill="var(--color-calls)"
                                            radius={[0, 5, 5, 0]}
                                            barSize={18}
                                        />
                                    </BarChart>
                                </ChartContainer>
                            </CardContent>
                        </Card>
                    </section>

                    <Card className="mt-5 overflow-hidden gap-0 py-0">
                        <Tabs
                            value={tableTab}
                            onValueChange={(value) => {
                                const nextTab = value as ToolTableTab;
                                if (
                                    nextTab === "parameterFailures" &&
                                    parameterFailureRequiresConfirmation &&
                                    !parameterFailureLongRangeConfirmed
                                ) {
                                    setParameterFailureConfirmationOpen(true);
                                    return;
                                }
                                setTableTab(nextTab);
                            }}
                        >
                            <div className="flex flex-col justify-between gap-4 border-b px-5 py-5 md:flex-row md:items-center">
                                <TabsList variant="line">
                                    <TabsTrigger value="ranking">
                                        <ArrowUpRight />
                                        调用排行
                                    </TabsTrigger>
                                    <TabsTrigger value="executionFailures">
                                        <ArrowDownRight />
                                        执行失败{" "}
                                        <Badge variant="secondary" className="ml-1">
                                            {failedTools.length}
                                        </Badge>
                                    </TabsTrigger>
                                    <TabsTrigger value="parameterFailures">
                                        <FileWarning />
                                        参数失败{" "}
                                        <Badge variant="secondary" className="ml-1">
                                            {parameterFailureData?.failures.length ?? 0}
                                        </Badge>
                                    </TabsTrigger>
                                </TabsList>
                                <div className="flex w-full flex-col gap-2 sm:flex-row md:w-auto">
                                    <Select
                                        value={category}
                                        onValueChange={(value) => setCategory(value as ToolCategoryFilter)}
                                    >
                                        <SelectTrigger className="w-full bg-white sm:w-48">
                                            <ListFilter className="size-4" />
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">全部类别 ({visibleAllCategoryCount})</SelectItem>
                                            {(Object.keys(TOOL_CATEGORY_LABELS) as ToolCategory[]).map((value) => (
                                                <SelectItem key={value} value={value}>
                                                    {TOOL_CATEGORY_LABELS[value]} ({visibleCategoryCounts[value]})
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <div className="relative w-full sm:w-72">
                                        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                                        <Input
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                            placeholder="搜索工具名..."
                                            className="pl-9"
                                        />
                                    </div>
                                </div>
                            </div>
                            <TabsContent value="ranking" className="m-0">
                                <ToolsTable
                                    tools={filteredTools}
                                    totalCalls={data?.totalCalls ?? 0}
                                    failuresOnly={false}
                                />
                            </TabsContent>
                            <TabsContent value="executionFailures" className="m-0">
                                <div className="flex flex-col justify-between gap-3 border-b bg-rose-50/60 px-5 py-3 text-xs text-rose-900 sm:flex-row sm:items-center">
                                    <span>
                                        统计已经产生 TOOL observation 并标记为 ERROR 的调用，不包含执行前的参数 Schema 校验失败。
                                    </span>
                                    <div className="shrink-0">
                                        <Select
                                            value={failureSort}
                                            onValueChange={(value) => setFailureSort(value as FailureSort)}
                                        >
                                            <SelectTrigger className="w-full bg-white sm:w-52">
                                                <ArrowUpDown className="size-4" />
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="failureCount">按失败次数排序</SelectItem>
                                                <SelectItem value="failureRate">按失败率排序</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <ToolsTable tools={failedTools} totalCalls={data?.totalCalls ?? 0} failuresOnly />
                            </TabsContent>
                            <TabsContent value="parameterFailures" className="m-0">
                                {!parameterFailureRangeSupported ? (
                                    <div className="grid min-h-72 place-items-center px-6 py-10 text-center">
                                        <div className="max-w-lg">
                                            <div className="mx-auto grid size-12 place-items-center rounded-full bg-amber-100 text-amber-700">
                                                <FileWarning className="size-6" />
                                            </div>
                                            <h3 className="mt-4 font-semibold">参数失败分析支持不超过 24 小时的范围</h3>
                                            <p className="mt-2 text-sm leading-6 text-muted-foreground">
                                                该分析需要逐页读取 Agent observation 的完整 output。超过支持区间时不会发起明细查询，
                                                避免占用 Langfuse 和本地服务资源。
                                            </p>
                                            <Button
                                                className="mt-5"
                                                onClick={() => {
                                                    setParameterFailureConfirmedSelectionKey(null);
                                                    setTimeRangeSelection("3h");
                                                }}
                                            >
                                                切换到过去 3 小时
                                            </Button>
                                        </div>
                                    </div>
                                ) : parameterFailureLoading ? (
                                    <div className="grid min-h-72 place-items-center px-6 text-center text-sm text-muted-foreground">
                                        <div className="w-full max-w-md">
                                            <RefreshCw className="mx-auto mb-3 size-5 animate-spin" />
                                            <p>正在分批扫描 Agent observations…</p>
                                            {parameterFailureData && (
                                                <>
                                                    <p className="mt-2 font-medium text-foreground">
                                                        {formatNumber(parameterFailureData.scannedObservations)} /{" "}
                                                        {formatNumber(parameterFailureData.availableObservations)}
                                                    </p>
                                                    <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-200">
                                                        <div
                                                            className="h-full rounded-full bg-emerald-500 transition-[width]"
                                                            style={{
                                                                width: `${Math.min(
                                                                    100,
                                                                    parameterFailureData.availableObservations
                                                                        ? (parameterFailureData.scannedObservations /
                                                                              parameterFailureData.availableObservations) *
                                                                              100
                                                                        : 0,
                                                                )}%`,
                                                            }}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ) : parameterFailureError ? (
                                    <div className="grid min-h-72 place-items-center px-6 py-10 text-center">
                                        <div className="max-w-lg">
                                            <AlertCircle className="mx-auto size-7 text-rose-600" />
                                            <h3 className="mt-3 font-semibold">参数失败数据加载失败</h3>
                                            <p className="mt-2 text-sm text-muted-foreground">{parameterFailureError}</p>
                                            <Button
                                                variant="outline"
                                                className="mt-5"
                                                onClick={() => setParameterFailureReload((value) => value + 1)}
                                            >
                                                从中断处继续
                                            </Button>
                                        </div>
                                    </div>
                                ) : parameterFailureData ? (
                                    <>
                                        <div
                                            className={
                                                parameterFailureData.complete
                                                    ? "flex flex-col justify-between gap-3 border-b bg-emerald-50/60 px-5 py-3 text-xs text-emerald-900 sm:flex-row sm:items-center"
                                                    : "flex flex-col justify-between gap-3 border-b bg-amber-50 px-5 py-3 text-xs text-amber-900 sm:flex-row sm:items-center"
                                            }
                                        >
                                            <span>
                                                {parameterFailureData.complete
                                                    ? `已完整扫描 ${formatNumber(parameterFailureData.scannedObservations)} 个 Agent observations。`
                                                    : `已扫描 ${formatNumber(parameterFailureData.scannedObservations)} / ${formatNumber(parameterFailureData.availableObservations)} 个 Agent observations，结果因 ${
                                                          parameterFailureData.truncationReason === "response_size_limit"
                                                              ? `${parameterFailureData.limits.maxPageBytes / 1024 / 1024} MB 单页大小上限`
                                                              : `${formatNumber(parameterFailureData.limits.maxObservations)} 条总扫描上限`
                                                      } 而不完整。`}
                                                <span className="ml-1">
                                                    仅统计 output.assistant.parts 中结构明确的静态工具参数失败，不返回原始参数。
                                                </span>
                                            </span>
                                            <Select
                                                value={parameterFailureSort}
                                                onValueChange={(value) =>
                                                    setParameterFailureSort(value as FailureSort)
                                                }
                                            >
                                                <SelectTrigger className="w-full shrink-0 bg-white sm:w-52">
                                                    <ArrowUpDown className="size-4" />
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="failureCount">按失败次数排序</SelectItem>
                                                    <SelectItem value="failureRate">按失败率排序</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <ParameterFailuresTable rows={parameterFailureRows} />
                                    </>
                                ) : (
                                    <div className="grid min-h-72 place-items-center px-6 text-sm text-muted-foreground">
                                        等待当前时间范围的工具统计加载完成…
                                    </div>
                                )}
                            </TabsContent>
                        </Tabs>
                    </Card>

                    <footer className="mt-5 flex flex-col justify-between gap-2 text-xs text-muted-foreground sm:flex-row">
                        <span>
                            分类快照：Skills {TOOL_CLASSIFICATION_SOURCE_VERSION}；其他仅包含 Registry
                            外的 Runtime 内置、额外注入和未识别工具
                        </span>
                        <span>
                            {data ? `最后刷新 ${new Date(data.refreshedAt).toLocaleString("zh-CN")}` : "等待连接"}
                        </span>
                    </footer>
                </main>

                <SettingsDialog
                    open={settingsOpen}
                    settings={settings}
                    onOpenChange={setSettingsOpen}
                    onSave={(nextSettings) => {
                        localStorage.setItem(STORAGE_KEY, JSON.stringify(nextSettings));
                        setSettings(nextSettings);
                        setSettingsOpen(false);
                    }}
                />
                <CustomTimeRangeDialog
                    open={customTimeRangeOpen}
                    range={customTimeRange}
                    onOpenChange={setCustomTimeRangeOpen}
                    onApply={(range) => {
                        setParameterFailureConfirmedSelectionKey(null);
                        setCustomTimeRange(range);
                        setTimeRangeSelection("custom");
                        setCustomTimeRangeOpen(false);
                    }}
                />
                <ParameterFailureConfirmationDialog
                    open={parameterFailureConfirmationOpen}
                    rangeLabel={activeTimeRange.shortLabel}
                    onCancel={() => {
                        setParameterFailureConfirmationOpen(false);
                        if (tableTab === "parameterFailures" && !parameterFailureLongRangeConfirmed) {
                            setTableTab("ranking");
                        }
                    }}
                    onConfirm={() => {
                        setParameterFailureConfirmedSelectionKey(activeTimeRange.selectionKey);
                        setParameterFailureConfirmationOpen(false);
                        setTableTab("parameterFailures");
                    }}
                />
            </div>
        </TooltipProvider>
    );
}
